import { useState } from "react";
import type { CopilotObservation } from "@CommonApi/merchflows/_merchflowId_/config-copilot";
import { Color, Flex, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { styledSafe } from "src/utils/styledComponent";
import type { SketchPalette } from "../store/sketchPalette";
import { Section, Wording } from "./OParts";
import { OSketch } from "./OSketch";

/** Lets each sentence through once per render, so no line shows twice on screen. */
export const sayOnce = () => {
  const said = new Set<string>();
  return (text: string | null | undefined): text is string => {
    const key = text
      ?.trim()
      .replace(/[.!\s]+$/, "")
      .toLowerCase();
    if (!key || said.has(key)) return false;
    said.add(key);
    return true;
  };
};

const Fold = styledSafe("button")`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  color: ${Color.textSecondary};
  font: inherit;
  font-size: 11px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    color: ${Color.primaryActive};
  }

  &:focus-visible {
    outline: 2px solid ${Color.primaryActive};
    outline-offset: 2px;
  }

  &[aria-expanded="true"] > span:first-child {
    transform: rotate(90deg);
  }
`;

interface SeenProps {
  observations: CopilotObservation[];
  isFolded: boolean;
  palette: SketchPalette;
  isWording?: boolean;
  foldLabel?: string;
}

/** "What I see": one line per observation, its sketches under it; one line after a run. */
export const OSeen = ({ observations, isFolded, palette, isWording, foldLabel }: SeenProps) => {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(!isFolded);
  if (observations.length === 0) return null;

  const fold = (
    <Fold
      type="button"
      aria-expanded={isOpen}
      aria-controls="o-seen"
      data-testid="o-seen-toggle"
      onClick={() => setIsOpen(!isOpen)}
    >
      <span aria-hidden="true">›</span>
      <span>{foldLabel ?? t("configCopilot.whatISeeCount", { count: observations.length })}</span>
    </Fold>
  );
  if (!isOpen) return fold;

  const list = (
    <Flex column gap="10px" id="o-seen" data-testid="o-seen">
      {observations.map((observation) => (
        <Flex
          key={observation.id}
          column={observation.sketch !== undefined}
          gap={observation.sketch ? "8px" : "12px"}
          align="start"
          justify="between"
          flexWrap="wrap"
          data-testid="o-observation"
        >
          <Flex flexGrow minWidth="180px" padding="2px 0 0 0">
            <Text variant="body2">
              <Wording aria-busy={isWording}>{observation.line}</Wording>
            </Text>
          </Flex>
          {observation.sketch && <OSketch pair={observation.sketch} palette={palette} />}
        </Flex>
      ))}
    </Flex>
  );

  return isFolded ? (
    <Flex column gap="8px">
      {fold}
      {list}
    </Flex>
  ) : (
    <Section title={t("configCopilot.whatISee")}>{list}</Section>
  );
};
